// src/pages/TrainingsPage.js
import { API_URL } from '../config/apiConfig';

import React, {useState, useEffect, useRef} from 'react';

// Material-UI
import {
    Container,
    Typography,
    Select,
    MenuItem,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControl,
    InputLabel,
    Box,
    Alert,
    List,
    ListItemButton,
    CircularProgress,
    Paper,
    Grid,
    Divider,
    IconButton,
    Chip,
    Radio,
    RadioGroup,
    FormControlLabel,
    useMediaQuery,
    useTheme,
    TextareaAutosize,
    Card,
    CardHeader,
    CardContent,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import TimerIcon from '@mui/icons-material/Timer';
import SportsMartialArtsIcon from '@mui/icons-material/SportsMartialArts';
import RowingIcon from '@mui/icons-material/Rowing';
import SportsGymnasticsIcon from '@mui/icons-material/SportsGymnastics';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

// FullCalendar React
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import AppTheme from "../shared-theme/AppTheme";
import DOMPurify from "dompurify";

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import Tooltip from '@mui/material/Tooltip'; // Kui ei ole juba

import WeightliftingPercentageCalculator from "../components/WeightliftingPercentageCalculator";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
// Styled components
const StyledContainer = styled(Container)(({theme}) => ({
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),

}));

const FormPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    width: '100%'
}));

const CalendarPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    overflow: 'hidden',
}));

const StyledListItem = styled(ListItemButton)(({theme}) => ({
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const StyledListContainer = styled(Paper)(({theme}) => ({
    border: `1px solid ${theme.palette.divider}`,
    maxHeight: 150,
    overflowY: 'auto',
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
}));

// Updated TypeChip component with stronger style overrides
const TypeChip = styled(Chip)(({theme, trainingtype}) => {
    let chipColor, textColor;

    switch (trainingtype) {
        case 'WOD':
            chipColor = '#0D47A1'; // Dark blue
            textColor = '#FFFFFF';
            break;
        case 'Weightlifting':
            chipColor = '#1B5E20'; // Dark green
            textColor = '#FFFFFF';
            break;
        case 'Cardio':
            chipColor = '#E65100'; // Orange
            textColor = '#FFFFFF';
            break;
        case 'Rowing':
            chipColor = '#4A148C'; // Dark purple
            textColor = '#FFFFFF';
            break;
        case 'Gymnastics':
            chipColor = '#880E4F'; // Dark pink
            textColor = '#FFFFFF';
            break;
        case 'Hyrox':
            chipColor = '#FF5722'; // Bright orange
            textColor = '#FFFFFF';
            break;
        case 'Mobility':
            chipColor = '#00838F'; // Dark cyan/teal
            textColor = '#FFFFFF';
            break;
        default:
            chipColor = '#424242'; // Dark gray
            textColor = '#FFFFFF';
    }

    return {
        backgroundColor: chipColor,
        '& .MuiChip-label': {
            padding: '0 8px',
            fontWeight: 700,
            fontSize: '1rem'
        }
    };
});

const getTypeIcon = (type) => {
    switch (type) {
        case 'WOD':
            return <TimerIcon/>;
        case 'Weightlifting':
            return <FitnessCenterIcon/>;
        case 'Cardio':
            return <DirectionsRunIcon/>;
        case 'Rowing':
            return <RowingIcon/>;
        case 'Gymnastics':
            return <SportsGymnasticsIcon/>;
        case 'Hyrox':
            return <EmojiEventsIcon/>;
        default:
            return <SportsMartialArtsIcon/>;
    }
};

export default function TrainingsPage() {
    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
    const MAX_UPLOAD_IMAGE_BYTES = 15 * 1024 * 1024;
    // --- State for creating new training ---
    const [trainingType, setTrainingType] = useState('');
    const [trainingDate, setTrainingDate] = useState('');
    const [showOptions, setShowOptions] = useState(false);

    // WOD-specific
    const [wodSearch, setWodSearch] = useState('');
    const [wodSearchResults, setWodSearchResults] = useState([]);
    const [wodName, setWodName] = useState('');
    const [wodType, setWodType] = useState('');
    const [wodDescription, setWodDescription] = useState('');
    const [wodScore, setWodScore] = useState('');
    const [wodScoreType, setWodScoreType] = useState('rx');

    // Weightlifting, Cardio, Other => exercises in one multiline
    const [exercises, setExercises] = useState('');

    // --- Loaded trainings for FullCalendar ---
    const [trainings, setTrainings] = useState([]);
    const [loadingTrainings, setLoadingTrainings] = useState(true);
    const [error, setError] = useState('');

    // --- Modal (Dialog) for viewing/editing a single training ---
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTraining, setModalTraining] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    // Form section visibility
    const [formExpanded, setFormExpanded] = useState(false);

    // Dialog states
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
    const [confirmDialogMessage, setConfirmDialogMessage] = useState('');

    // New Add to Records with Comment dialog states
    const [addToRecordsDialogOpen, setAddToRecordsDialogOpen] = useState(false);
    const [recordComment, setRecordComment] = useState('');

    // Theme and responsive design
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [hasInitializedExercises, setHasInitializedExercises] = useState(false);
    // API_URL imported from config

    // Uued state-id kuu treeningute modali jaoks
    const [monthTrainingsModalOpen, setMonthTrainingsModalOpen] = useState(false);
    const [selectedType, setSelectedType] = useState('');
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const exercisesRef = useRef(null);
    const modalExercisesRef = useRef(null);
    const [selectedColorOption, setSelectedColorOption] = useState(null);
    const [modalWorkoutImage, setModalWorkoutImage] = useState(null);
    const [modalWorkoutImageFile, setModalWorkoutImageFile] = useState(null);
    const [modalWorkoutImagePreview, setModalWorkoutImagePreview] = useState('');
    const [removeModalWorkoutImage, setRemoveModalWorkoutImage] = useState(false);
    const [modalImageError, setModalImageError] = useState('');
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [isSavingEdit, setIsSavingEdit] = useState(false);

    const colorOptions = [
        { name: "Red", color: "#c62929" },
        { name: "Green", color: "#1a7e1a" },
        { name: "Blue", color: "#3434d6" },
        { name: "Yellow", color: "#a1a166" },
        { name: "Orange", color: "#ffa500" },
        { name: "Purple", color: "#800080" },
    ];

    // --- 1) Load trainings on mount ---
    useEffect(() => {
        loadTrainings();
    }, []);

    useEffect(() => {
        if (modalOpen && modalTraining?.id) {
            loadTrainingWorkoutImage(modalTraining.id);
        }
    }, [modalOpen, modalTraining?.id]);

    useEffect(() => () => {
        if (modalWorkoutImagePreview) {
            URL.revokeObjectURL(modalWorkoutImagePreview);
        }
    }, [modalWorkoutImagePreview]);

    // Show success message
    const showSuccessMessage = (message) => {
        setDialogMessage(message);
        setSuccessDialogOpen(true);
    };

    // Show error message
    const showErrorMessage = (message) => {
        setDialogMessage(message);
        setErrorDialogOpen(true);
    };

    // Show confirmation dialog
    const showConfirmDialog = (title, message, onConfirm) => {
        setConfirmDialogTitle(title);
        setConfirmDialogMessage(message);
        setConfirmAction(() => onConfirm);
        setConfirmDialogOpen(true);
    };

    async function loadTrainings() {
        try {
            setLoadingTrainings(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/trainings`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Failed to load trainings');
            }
            const data = await response.json();
            setTrainings(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingTrainings(false);
        }
    }

    async function loadTrainingWorkoutImage(trainingId) {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/training/${trainingId}/workout-image`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 404) {
                setModalWorkoutImage(null);
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to load training image');
            }

            const imageData = await response.json();
            setModalWorkoutImage(imageData);
        } catch (err) {
            setModalWorkoutImage(null);
        }
    }

    const compressImageInBrowser = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const maxDim = 1600;
                const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
                const width = Math.round(img.width * ratio);
                const height = Math.round(img.height * ratio);
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) return reject(new Error('Failed to prepare image compression'));
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) return reject(new Error('Failed to compress image'));
                        const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
                        resolve(compressed);
                    },
                    'image/jpeg',
                    0.82
                );
            };
            img.onerror = () => reject(new Error('Invalid image file'));
            img.src = String(reader.result);
        };
        reader.onerror = () => reject(new Error('Failed to read image file'));
        reader.readAsDataURL(file);
    });

    async function handleModalWorkoutImageChange(event) {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            setModalImageError('Only JPG, PNG, WEBP, GIF and AVIF image formats are allowed.');
            return;
        }

        if (file.size > MAX_UPLOAD_IMAGE_BYTES) {
            setModalImageError('Image is too large. Maximum file size is 15MB.');
            return;
        }

        setModalImageError('');
        try {
            const compressedFile = await compressImageInBrowser(file);
            const objectUrl = URL.createObjectURL(compressedFile);
            setModalWorkoutImageFile(compressedFile);
            setModalWorkoutImagePreview(objectUrl);
            setRemoveModalWorkoutImage(false);
        } catch (error) {
            setModalImageError(error.message || 'Failed to process selected image.');
        }
    }

    function handleRemoveModalWorkoutImage() {
        if (modalWorkoutImagePreview) {
            URL.revokeObjectURL(modalWorkoutImagePreview);
        }
        setModalWorkoutImageFile(null);
        setModalWorkoutImagePreview('');
        setRemoveModalWorkoutImage(true);
        setModalImageError('');
    }

    // --- 2) Training type selection ---
    function handleTrainingTypeChange(e) {
        const val = e.target.value;
        setTrainingType(val);
        setShowOptions(!!val);
        if (!val) {
            setTrainingDate('');
        }
    }

    // --- 3) WOD search logic ---
    useEffect(() => {
        if (!wodSearch.trim()) {
            setWodSearchResults([]);
            return;
        }
        const fetchWods = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/wods/search-default-wods?q=${encodeURIComponent(wodSearch.toUpperCase())}`, {
                    headers: {Authorization: `Bearer ${token}`},
                });
                if (response.ok) {
                    const results = await response.json();
                    setWodSearchResults(results.slice(0, 10)); // show top 10
                }
            } catch (error) {
                console.error('Error fetching WODs:', error);
            }
        };
        fetchWods();
    }, [wodSearch]);

    function handleSelectWod(wod) {
        setWodName(wod.name);
        setWodType(wod.type || '');
        // transform description for multiline
        const replaced = wod.description
            .replace(/:/g, ':\n')
            .replace(/,/g, '\n');
        setWodDescription(replaced);
        setWodSearchResults([]);
    }

    function initDOMPurify() {
        // Configure DOMPurify to allow style attributes and common formatting tags
        DOMPurify.addHook('afterSanitizeAttributes', function (node) {
            // Allow all style attributes
            if (node.hasAttribute('style')) {
                // Do nothing, let the style attribute remain
                return;
            }
        });
    }

// Call this in a useEffect to initialize DOMPurify when the component mounts
    useEffect(() => {
        initDOMPurify();
    }, []);

    // --- 4) Submit new training ---
    async function handleSubmitTraining(e) {
        e.preventDefault();
        if (!trainingType || !trainingDate) {
            setError('Please select training type and date!');
            return;
        }

        let payload = {
            type: trainingType,
            date: trainingDate,
        };

        if (trainingType === 'WOD') {
            payload = {
                ...payload,
                wodName: wodName.toUpperCase(),
                wodType,
                exercises: wodDescription,
                score: wodScore,
                scoreType: wodScoreType,
            };
        } else {
            payload = {
                ...payload,
                exercises,
            };
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/training`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload),
            });
            const result = await response.json();
            if (!response.ok) {
                showErrorMessage(result.error || 'Error creating training');
                return;
            }
            // If server returns updated training list, set it:
            setTrainings(result.trainings || []);

            // Clear form
            setTrainingType('');
            setTrainingDate('');
            setWodName('');
            setWodType('');
            setWodDescription('');
            setWodScore('');
            setWodScoreType('rx');
            setExercises('');
            setShowOptions(false);
            setError('');

            // Show success message
            showSuccessMessage('Training created successfully!');
        } catch (error) {
            showErrorMessage('Error: ' + error.message);
        }
    }

    // --- 5) FullCalendar event logic ---
    // Convert trainings -> FullCalendar events
    function getCalendarEvents() {
        return trainings.map((t) => {
            let bgColor;
            switch (t.type) {
                case 'WOD':
                    bgColor = '#0D47A1'; // Dark blue (matches chip)
                    break;
                case 'Weightlifting':
                    bgColor = '#1B5E20'; // Dark green (matches chip)
                    break;
                case 'Cardio':
                    bgColor = '#E65100'; // Orange (matches chip)
                    break;
                case 'Rowing':
                    bgColor = '#4A148C'; // Dark purple (matches chip)
                    break;
                case 'Gymnastics':
                    bgColor = '#880E4F'; // Dark pink (matches chip)
                    break;
                case 'Hyrox':
                    bgColor = '#FF5722'; // Bright orange (matches chip)
                    break;
                case 'Mobility':
                    bgColor = '#00838F'; // Dark cyan/teal (matches chip)
                    break;
                default:
                    bgColor = '#424242'; // Dark gray (matches chip)
            }

            return {
                id: t.id,
                title: t.type,
                start: t.date,
                backgroundColor: bgColor,
                borderColor: '#0d1117',
                extendedProps: {training: t},
            };
        });
    }

    // On eventClick -> open modal
    function handleEventClick(clickInfo) {
        const training = clickInfo.event.extendedProps.training;
        openTrainingModal(training);
    }

    // eventContent, to replicate the "colored bar"
    function renderEventContent(arg) {
        let backgroundColor;
        switch (arg.event.title) {
            case 'WOD':
                backgroundColor = '#0D47A1'; // Dark blue (matches chip)
                break;
            case 'Weightlifting':
                backgroundColor = '#1B5E20'; // Dark green (matches chip)
                break;
            case 'Cardio':
                backgroundColor = '#E65100'; // Orange (matches chip)
                break;
            case 'Rowing':
                backgroundColor = '#4A148C'; // Dark purple (matches chip)
                break;
            case 'Gymnastics':
                backgroundColor = '#880E4F'; // Dark pink (matches chip)
                break;
            case 'Hyrox':
                backgroundColor = '#FF5722'; // Bright orange (matches chip)
                break;
            case 'Mobility':
                backgroundColor = '#00838F'; // Dark cyan/teal (matches chip)
                break;
            default:
                backgroundColor = '#424242'; // Dark gray (matches chip)
        }

        return {
            html: `<div style="width:100%;height:14px;background-color:${backgroundColor};border-radius:3px;margin-bottom:2px; border: #0d1117"></div>`,
        };
    }

    // --- 6) Modal open/close ---
    function openTrainingModal(training) {
        setModalTraining(training);
        setIsEditing(false);
        setModalWorkoutImageFile(null);
        setModalWorkoutImagePreview('');
        setRemoveModalWorkoutImage(false);
        setModalImageError('');
        setModalOpen(true);
    }

    function closeModal() {
        if (modalWorkoutImagePreview) {
            URL.revokeObjectURL(modalWorkoutImagePreview);
        }
        setModalWorkoutImageFile(null);
        setModalWorkoutImagePreview('');
        setRemoveModalWorkoutImage(false);
        setModalImageError('');
        setModalWorkoutImage(null);
        setImageDialogOpen(false);
        if (isEditing) {

            setModalOpen(false);
            setModalTraining(null);
            setIsEditing(false);


        } else {
            setModalOpen(false);
            setModalTraining(null);
        }
    }

    // --- 7) Edit / Save in modal ---
    function handleEdit() {
        setModalImageError('');
        setIsEditing(true);
    }

    function handleCancelEdit() {
        if (modalWorkoutImagePreview) {
            URL.revokeObjectURL(modalWorkoutImagePreview);
        }
        setModalWorkoutImageFile(null);
        setModalWorkoutImagePreview('');
        setRemoveModalWorkoutImage(false);
        setModalImageError('');
        setIsEditing(false);
        if (modalTraining?.id) {
            loadTrainingWorkoutImage(modalTraining.id);
        }
    }

    async function handleSave() {
        if (!modalTraining) return;
        if (modalImageError) return;
        if (isSavingEdit) return;

        // Read the current HTML content from the contentEditable field
        const currentExercises = modalExercisesRef.current
            ? modalExercisesRef.current.innerHTML
            : modalTraining.exercises;

        // Prepare the data for submission
        const trainingData = {
            ...modalTraining,
            exercises: currentExercises, // Use the current HTML content
            // Make sure date is in the correct format
            date: modalTraining.date?.split('T')[0] || modalTraining.date
        };

        setIsSavingEdit(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/training/${modalTraining.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(trainingData),
            });
            if (!response.ok) {
                const resErr = await response.json();
                showErrorMessage('Error: ' + (resErr.error || 'Unknown'));
                return;
            }

            if (removeModalWorkoutImage) {
                await fetch(`${API_URL}/training/${modalTraining.id}/workout-image`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                });
            }

            if (modalWorkoutImageFile) {
                const formData = new FormData();
                formData.append('image', modalWorkoutImageFile);
                const imageRes = await fetch(`${API_URL}/training/${modalTraining.id}/workout-image`, {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token
                    },
                    body: formData
                });
                if (!imageRes.ok) {
                    const imageErr = await imageRes.json().catch(() => ({}));
                    showErrorMessage(imageErr.error || 'Training image upload failed');
                    return;
                }
            }

            setIsEditing(false);
            closeModal();
            await loadTrainings();
            showSuccessMessage("Training updated successfully!");
        } catch (err) {
            showErrorMessage('Error saving training: ' + err.message);
        } finally {
            setIsSavingEdit(false);
        }
    }

    // Add to records if WOD - MODIFIED
    function handleAddToRecords() {
        if (!modalTraining) return;
        if (modalTraining.type !== 'WOD' || !modalTraining.wodName) return;

        // Reset comment and open the new dialog
        setRecordComment('');
        setAddToRecordsDialogOpen(true);
    }

    // New function to handle the actual record submission with comment
    async function submitAddToRecords() {
        const recordData = {
            type: 'WOD',
            name: modalTraining.wodName,
            date: modalTraining.date,
            score: modalTraining.score,
            scoreType: modalTraining.scoreType,
            comment: recordComment // Include the comment
        };

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/records`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(recordData),
            });

            if (!response.ok) {
                const resErr = await response.json();
                showErrorMessage('Error: ' + (resErr.error || 'Could not add to records'));
                return;
            }

            showSuccessMessage('WOD added to records successfully!');
            setAddToRecordsDialogOpen(false);
        } catch (err) {
            showErrorMessage('Error: ' + err.message);
        }
    }

    // Update modal training field
    function updateModalField(field, value) {
        setModalTraining((prev) => ({
            ...prev,
            [field]: value,
        }));
    }

    const getScoreTypeDisplay = (scoreType) => {
        if (!scoreType) return '';
        switch (scoreType.toLowerCase()) {
            case 'rx':
                return 'RX';
            case 'sc':
                return 'SC';
            case 'beg':
                return 'BEGINNER';
            default:
                return scoreType.toUpperCase();
        }
    };

    // For updating exercises in modal
    function updateExercise(index, newValue) {
        setModalTraining((prev) => {
            const newExercises = [...(prev.exercises || [])];
            newExercises[index] = {exerciseData: newValue};
            return {...prev, exercises: newExercises};
        });
    }

    useEffect(() => {
        if (exercisesRef.current && exercisesRef.current.innerHTML !== exercises) {
            exercisesRef.current.innerHTML = exercises;
        }
    }, [exercises]);

    useEffect(() => {
        if (modalExercisesRef.current && modalTraining?.exercises && isEditing && !hasInitializedExercises) {
            let exercisesContent = '';

            if (typeof modalTraining.exercises === 'string') {
                exercisesContent = modalTraining.exercises
                    .replace(/\r\n/g, '<br>')
                    .replace(/\n/g, '<br>')
                    .replace(/\r/g, '<br>');
            } else if (Array.isArray(modalTraining.exercises)) {
                exercisesContent = modalTraining.exercises
                    .map(ex => {
                        const data = ex.exerciseData || '';
                        return data
                            .replace(/\r\n/g, '<br>')
                            .replace(/\n/g, '<br>')
                            .replace(/\r/g, '<br>');
                    })
                    .join('<br>');
            }

            modalExercisesRef.current.innerHTML = exercisesContent;
            setHasInitializedExercises(true);
        }

        // Reset flag when edit mode closes
        if (!isEditing) {
            setHasInitializedExercises(false);
        }
    }, [modalTraining?.exercises, isEditing, hasInitializedExercises]);

    // Toggle form visibility
    const toggleForm = () => {
        setFormExpanded(!formExpanded);
    };

    // Apply formatting to contentEditable fields
    // Apply formatting to contentEditable fields with toggle support
    const applyFormatting = (formatType, value = null, fieldRef) => {
        const element = fieldRef.current;
        if (!element) return;

        element.focus();

        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);

        if (!element.contains(range.commonAncestorContainer)) return;

        const selectedText = range.toString();
        if (!selectedText) return;

        // Check if we need to remove formatting instead of adding it
        let parentNode = range.commonAncestorContainer;

        // Find the parent formatting node if it exists
        while (parentNode && parentNode !== element) {
            if (parentNode.nodeType === 1) { // Element node
                let shouldRemove = false;

                if (formatType === 'bold' && parentNode.tagName === 'B') {
                    shouldRemove = true;
                } else if (formatType === 'italic' && parentNode.tagName === 'I') {
                    shouldRemove = true;
                } else if (formatType === 'color' && parentNode.tagName === 'SPAN' && parentNode.style.color) {
                    shouldRemove = true;
                }

                if (shouldRemove) {
                    // Remove the formatting by replacing the node with its contents
                    const textContent = parentNode.textContent;
                    const textNode = document.createTextNode(textContent);
                    parentNode.parentNode.replaceChild(textNode, parentNode);

                    // Update state
                    const updatedHTML = element.innerHTML;
                    if (fieldRef === exercisesRef) {
                        setExercises(updatedHTML);
                    } else if (fieldRef === modalExercisesRef) {
                        setModalTraining((prev) => ({
                            ...prev,
                            exercises: updatedHTML
                        }));
                    }
                    return;
                }
            }
            parentNode = parentNode.parentNode;
        }

        // If we're here, we need to ADD formatting
        let newNode;
        switch (formatType) {
            case 'bold':
                newNode = document.createElement('b');
                break;
            case 'italic':
                newNode = document.createElement('i');
                break;
            case 'color':
                newNode = document.createElement('span');
                newNode.style.color = value;
                break;
            default:
                return;
        }

        const contents = range.extractContents();
        newNode.appendChild(contents);
        range.insertNode(newNode);

        range.setStartAfter(newNode);
        range.setEndAfter(newNode);
        selection.removeAllRanges();
        selection.addRange(range);

        // Update state with new HTML
        const updatedHTML = element.innerHTML;
        if (fieldRef === exercisesRef) {
            setExercises(updatedHTML);
        } else if (fieldRef === modalExercisesRef) {
            setModalTraining((prev) => ({
                ...prev,
                exercises: updatedHTML
            }));
        }
    };


// Funktsioon kuu treeningute filtreerimiseks tüübi järgi
    const getMonthTrainingsByType = (type) => {
        const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);

        return trainings
            .filter(training => {
                const trainingDate = new Date(training.date);
                return training.type === type &&
                    trainingDate >= startOfMonth &&
                    trainingDate <= endOfMonth;
            })
            .sort((a, b) => new Date(b.date) - new Date(a.date)); // Hilisemad esimesena
    };

// Funktsioon modali avamiseks
    const handleTypeChipClick = (type) => {
        setSelectedType(type);
        setMonthTrainingsModalOpen(true);
    };

    async function handleDelete() {
        if (!modalTraining) return;

        // Ask for confirmation before deleting
        showConfirmDialog(
            "Delete Training",
            "Are you sure you want to delete this training? This action cannot be undone.",
            async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API_URL}/training/${modalTraining.id}`, {
                        method: 'DELETE',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (!response.ok) {
                        const resErr = await response.json();
                        showErrorMessage('Error: ' + (resErr.error || 'Unknown error'));
                        return;
                    }

                    // Close modal and reload trainings
                    closeModal();
                    await loadTrainings();
                    showSuccessMessage("Training successfully deleted!");
                } catch (err) {
                    showErrorMessage('Error deleting training: ' + err.message);
                }
            }
        );
    }

    return (
        <AppTheme>
            <StyledContainer maxWidth="lg" sx={{width: '100%', pl: 0, pr: 0}}>
                <Typography variant="h4" gutterBottom align="center" fontWeight="bold">
                    TRAININGS
                </Typography>

                {error && (
                    <Alert
                        severity="error"
                        sx={{width: '100%'}}
                        onClose={() => setError('')}
                    >
                        {error}
                    </Alert>
                )}

                {/* Add Training Form Section */}
                <FormPaper>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                        <Typography variant="h6" fontWeight="medium">
                            Add New Training
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={formExpanded ? <CloseIcon/> : <AddIcon/>}
                            onClick={toggleForm}
                            size="small"
                        >
                            {formExpanded ? 'Hide Form' : 'Show Form'}
                        </Button>
                    </Box>

                    {formExpanded && (
                        <Box component="form" onSubmit={handleSubmitTraining}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel id="training-type-label">Training Type</InputLabel>
                                        <Select
                                            labelId="training-type-label"
                                            value={trainingType}
                                            label="Training Type"
                                            onChange={handleTrainingTypeChange}
                                        >
                                            <MenuItem value="">-- Select Training Type --</MenuItem>
                                            <MenuItem value="WOD">
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <TimerIcon sx={{mr: 1}}/>
                                                    WOD
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Weightlifting">
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <FitnessCenterIcon sx={{mr: 1}}/>
                                                    Weightlifting
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Cardio">
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <DirectionsRunIcon sx={{mr: 1}}/>
                                                    Cardio
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Rowing">
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <RowingIcon sx={{mr: 1}}/>
                                                    Rowing
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Gymnastics">
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <SportsGymnasticsIcon sx={{mr: 1}}/>
                                                    Gymnastics
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Hyrox">
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <EmojiEventsIcon sx={{mr: 1}}/>
                                                    Hyrox
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Mobility">
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <SportsMartialArtsIcon sx={{mr: 1}}/>
                                                    Mobility
                                                </Box>
                                            </MenuItem>

                                            <MenuItem value="Other">
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <SportsMartialArtsIcon sx={{mr: 1}}/>
                                                    Other
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {showOptions && (
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Date"
                                            value={trainingDate}
                                            onChange={(e) => setTrainingDate(e.target.value)}
                                            InputLabelProps={{shrink: true}}
                                            required
                                        />
                                    </Grid>
                                )}
                            </Grid>

                            {/* WOD stuff */}
                            {trainingType === 'WOD' && showOptions && (
                                <Box sx={{mt: 3}}>
                                    <Divider sx={{my: 2}}/>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            {/* Search default WODs */}
                                            <TextField
                                                fullWidth
                                                label="Search Default WODs"
                                                value={wodSearch}
                                                onChange={(e) => setWodSearch(e.target.value)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <SearchIcon sx={{color: 'action.active', mr: 1}}/>
                                                    ),
                                                }}
                                            />
                                            {wodSearchResults.length > 0 && (
                                                <StyledListContainer>
                                                    <List dense>
                                                        {wodSearchResults.map((w) => (
                                                            <StyledListItem key={w.name}
                                                                            onClick={() => handleSelectWod(w)}>
                                                                <Typography>{w.name}</Typography>
                                                            </StyledListItem>
                                                        ))}
                                                    </List>
                                                </StyledListContainer>
                                            )}
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="WOD Name"
                                                value={wodName}
                                                onChange={(e) => setWodName(e.target.value)}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl component="fieldset">
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    WOD Type
                                                </Typography>
                                                <RadioGroup
                                                    row
                                                    name="wod-type"
                                                    value={wodType}
                                                    onChange={(e) => setWodType(e.target.value)}
                                                >
                                                    {['For Time', 'EMOM', 'Tabata', 'AMRAP', 'For Load', 'For Quality', 'NONE'].map((val) => (
                                                        <FormControlLabel
                                                            key={val}
                                                            value={val}
                                                            control={<Radio/>}
                                                            label={val}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    WOD Description
                                                </Typography>
                                                <TextareaAutosize
                                                    minRows={5}
                                                    maxRows={20}
                                                    value={wodDescription}
                                                    onChange={(e) => {
                                                        const text = e.target.value;
                                                        // Set a 2000 character limit (adjust as needed)
                                                        if (text.length <= 2000) {
                                                            setWodDescription(text);
                                                        }
                                                    }}
                                                    placeholder="Enter exercises and reps"
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        borderRadius: '4px',
                                                        borderColor: '#AAAAAA',
                                                        fontSize: '14px',
                                                        lineHeight: '1.5',
                                                        resize: 'vertical',
                                                        fontFamily: 'inherit'
                                                    }}
                                                />
                                                {wodDescription.length >= 1500 && (
                                                    <Typography
                                                        variant="caption"
                                                        color={wodDescription.length >= 2000 ? "error" : "warning"}
                                                        sx={{mt: 1}}
                                                    >
                                                        {wodDescription.length >= 5000
                                                            ? "Maximum 2000 characters allowed. You've reached the limit."
                                                            : `${2000 - wodDescription.length} characters remaining.`}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Score"
                                                value={wodScore}
                                                onChange={(e) => setWodScore(e.target.value)}
                                                placeholder="Enter your result"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel id="wod-score-type-label">Score Type</InputLabel>
                                                <Select
                                                    labelId="wod-score-type-label"
                                                    value={wodScoreType}
                                                    label="Score Type"
                                                    onChange={(e) => setWodScoreType(e.target.value)}
                                                >
                                                    <MenuItem value="rx">RX</MenuItem>
                                                    <MenuItem value="sc">SC</MenuItem>
                                                    <MenuItem value="beg">BEGINNER</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Weightlifting, Cardio, Other */}
                            {['Weightlifting', 'Cardio', 'Rowing', 'Gymnastics', 'Hyrox', 'Mobility', 'Other'].includes(trainingType) && showOptions && (
                                <Box sx={{mt: 3}}>
                                    <Divider sx={{my: 2}}/>

                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Exercises
                                    </Typography>

                                    {/* Formatting toolbar */}
                                    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ mr: 1 }}>Formatting:</Typography>
                                        <Tooltip title="Bold">
                                            <IconButton size="small" onClick={() => applyFormatting('bold', null, exercisesRef)}>
                                                <FormatBoldIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Italic">
                                            <IconButton size="small" onClick={() => applyFormatting('italic', null, exercisesRef)}>
                                                <FormatItalicIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Box sx={{ position: 'relative' }}>
                                            <Tooltip title="Text Color">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setSelectedColorOption(prev => prev === 'exercises' ? null : 'exercises')}
                                                >
                                                    <FormatColorTextIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            {selectedColorOption === 'exercises' && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    zIndex: 1000,
                                                    width: '200px',
                                                    backgroundColor: 'white',
                                                    border: '1px solid #ddd',
                                                    borderRadius: 1,
                                                    boxShadow: 3,
                                                    p: 1
                                                }}>
                                                    <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                                                        Select color:
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {colorOptions.map((option) => (
                                                            <Tooltip key={option.name} title={option.name}>
                                                                <Box
                                                                    sx={{
                                                                        width: 24,
                                                                        height: 24,
                                                                        backgroundColor: option.color,
                                                                        border: '1px solid #888',
                                                                        borderRadius: 0.5,
                                                                        cursor: 'pointer'
                                                                    }}
                                                                    onClick={() => {
                                                                        applyFormatting('color', option.color, exercisesRef);
                                                                        setSelectedColorOption(null);
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    <FormControl fullWidth>
                                        <Box
                                            ref={exercisesRef}
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={(e) => {
                                                const value = e.currentTarget.innerHTML;
                                                if (value.length <= 2000) {
                                                    setExercises(value);
                                                }
                                            }}
                                            data-placeholder="Enter your exercises, sets, reps and weights (select text to format)"
                                            sx={{
                                                minHeight: '120px',
                                                maxHeight: '400px',
                                                overflowY: 'auto',
                                                padding: '10px',
                                                border: '1px solid #AAAAAA',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                lineHeight: '1.5',
                                                fontFamily: 'inherit',
                                                '&:focus': {
                                                    outline: 'none',
                                                    borderColor: '#1976d2',
                                                    borderWidth: '2px'
                                                },
                                                '&:empty:before': {
                                                    content: 'attr(data-placeholder)',
                                                    color: '#999'
                                                }
                                            }}
                                        />
                                        {exercises.length >= 1800 && (
                                            <Typography
                                                variant="caption"
                                                color={exercises.length >= 2000 ? "error" : "warning"}
                                                sx={{mt: 1}}
                                            >
                                                {exercises.length >= 2000
                                                    ? "Maximum 2000 characters allowed. You've reached the limit."
                                                    : `${2000 - exercises.length} characters remaining.`}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Box>
                            )}

                            {showOptions && (
                                <Box sx={{mt: 3, display: 'flex', justifyContent: 'flex-end'}}>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        variant="contained"
                                        startIcon={<SaveIcon/>}
                                        size="large"
                                    >
                                        Save Training
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </FormPaper>

                {/* Training Calendar Section */}
                <CalendarPaper>
                    <Box sx={{mb: 2}}>
                        <Typography variant="h6" fontWeight="medium">
                            Training Calendar
                        </Typography>
                    </Box>

                    {/* Legend */}
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3}}>
                        <TypeChip
                            label="WOD"
                            icon={<TimerIcon/>}
                            trainingtype="WOD"
                            size="medium"
                            onClick={() => handleTypeChipClick('WOD')}
                            sx={{
                                '& .MuiChip-label': {color: '#FFFFFF !important', fontSize: '0.7rem'},
                                '& .MuiSvgIcon-root': {color: '#FFFFFF !important'},
                                cursor: 'pointer',
                                '&:hover': {opacity: 0.8}
                            }}
                        />
                        <TypeChip
                            label="Weightlifting"
                            icon={<FitnessCenterIcon/>}
                            trainingtype="Weightlifting"
                            size="medium"
                            onClick={() => handleTypeChipClick('Weightlifting')}
                            sx={{
                                '& .MuiChip-label': {color: '#FFFFFF !important', fontSize: '0.7rem'},
                                '& .MuiSvgIcon-root': {color: '#FFFFFF !important'},
                                cursor: 'pointer',
                                '&:hover': {opacity: 0.8}
                            }}
                        />
                        <TypeChip
                            label="Cardio"
                            icon={<DirectionsRunIcon sx={{color: 'inherit'}}/>}
                            trainingtype="Cardio"
                            size="medium"
                            onClick={() => handleTypeChipClick('Cardio')}
                            sx={{
                                '& .MuiChip-label': {color: '#FFFFFF !important', fontSize: '0.7rem'},
                                '& .MuiSvgIcon-root': {color: '#FFFFFF !important'},
                                cursor: 'pointer',
                                '&:hover': {opacity: 0.8}
                            }}
                        />
                        <TypeChip
                            label="Rowing"
                            icon={<RowingIcon sx={{color: 'inherit'}}/>}
                            trainingtype="Rowing"
                            size="medium"
                            onClick={() => handleTypeChipClick('Rowing')}
                            sx={{
                                '& .MuiChip-label': {color: '#FFFFFF !important', fontSize: '0.7rem'},
                                '& .MuiSvgIcon-root': {color: '#FFFFFF !important'},
                                cursor: 'pointer',
                                '&:hover': {opacity: 0.8}
                            }}
                        />
                        <TypeChip
                            label="Gymnastics"
                            icon={<SportsGymnasticsIcon sx={{color: 'inherit'}}/>}
                            trainingtype="Gymnastics"
                            size="medium"
                            onClick={() => handleTypeChipClick('Gymnastics')}
                            sx={{
                                '& .MuiChip-label': {color: '#FFFFFF !important', fontSize: '0.7rem'},
                                '& .MuiSvgIcon-root': {color: '#FFFFFF !important'},
                                cursor: 'pointer',
                                '&:hover': {opacity: 0.8}
                            }}
                        />
                        <TypeChip
                            label="Hyrox"
                            icon={<EmojiEventsIcon sx={{color: 'inherit'}}/>}
                            trainingtype="Hyrox"
                            size="medium"
                            onClick={() => handleTypeChipClick('Hyrox')}
                            sx={{
                                '& .MuiChip-label': {color: '#FFFFFF !important', fontSize: '0.7rem'},
                                '& .MuiSvgIcon-root': {color: '#FFFFFF !important'},
                                cursor: 'pointer',
                                '&:hover': {opacity: 0.8}
                            }}
                        />
                        <TypeChip
                            label="Other"
                            icon={<SportsMartialArtsIcon sx={{color: 'inherit'}}/>}
                            trainingtype="Other"
                            size="medium"
                            onClick={() => handleTypeChipClick('Other')}
                            sx={{
                                '& .MuiChip-label': {color: '#FFFFFF !important', fontSize: '0.7rem'},
                                '& .MuiSvgIcon-root': {color: '#FFFFFF !important'},
                                cursor: 'pointer',
                                '&:hover': {opacity: 0.8}
                            }}
                        />
                    </Box>

                    {/* Error or loading indicator */}
                    {loadingTrainings && (
                        <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                            <CircularProgress/>
                        </Box>
                    )}

                    {/* FULLCALENDAR to display trainings */}
                    {!loadingTrainings && (
                        <Box sx={{'.fc': {fontFamily: 'inherit'}, '.fc-toolbar-title': {fontSize: '1.2em'}}}>
                            <FullCalendar
                                plugins={[dayGridPlugin]}
                                initialView="dayGridMonth"
                                events={getCalendarEvents()}
                                eventClick={handleEventClick}
                                eventContent={renderEventContent}
                                height="auto"
                                firstDay={1}
                                headerToolbar={{
                                    left: 'prev',
                                    center: 'title',
                                    right: 'today next'
                                }}
                                datesSet={(dateInfo) => {
                                    // Kasuta kalendri aktuaalset kuupäeva, mitte vaate algust
                                    const currentDate = dateInfo.view.calendar.getDate();
                                    setCurrentMonth(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
                                }}
                            />
                        </Box>
                    )}
                </CalendarPaper>

                {/* MODAL (Dialog) for training details */}
                <Dialog
                    open={modalOpen}
                    onClose={closeModal}
                    maxWidth="md"
                    fullWidth
                    fullScreen={isMobile}
                >
                    <DialogTitle>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                {modalTraining && (
                                    <>
                                        {getTypeIcon(modalTraining.type)}
                                        <Typography variant="h6" component="div" sx={{ml: 1}}>
                                            {modalTraining.type === 'WOD' ? modalTraining.wodName || 'Training Details' : 'Training Details'}
                                        </Typography>
                                        <TypeChip
                                            label={modalTraining.type}
                                            trainingtype={modalTraining.type}
                                            size="small"
                                            sx={{
                                                '& .MuiChip-label': {color: '#FFFFFF !important', fontSize: '0.7rem'},
                                                '& .MuiSvgIcon-root': {color: '#FFFFFF !important'},
                                                ml: 2
                                            }}
                                        />
                                    </>
                                )}
                            </Box>
                            <IconButton onClick={closeModal} edge="end">
                                <CloseIcon/>
                            </IconButton>
                        </Box>
                    </DialogTitle>

                    <DialogContent dividers>
                        {modalTraining && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Date"
                                        type="date"
                                        value={modalTraining.date?.split('T')[0] || ''}
                                        fullWidth
                                        onChange={(e) => updateModalField('date', e.target.value)}
                                        disabled={!isEditing}
                                        InputLabelProps={{shrink: true}}
                                    />
                                </Grid>

                                {/* If WOD */}
                                {modalTraining.type === 'WOD' && (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="WOD Name"
                                                value={modalTraining.wodName || ''}
                                                fullWidth
                                                disabled={!isEditing}
                                                onChange={(e) => updateModalField('wodName', e.target.value)}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="WOD Type"
                                                value={modalTraining.wodType || ''}
                                                fullWidth
                                                disabled={!isEditing}
                                                onChange={(e) => updateModalField('wodType', e.target.value)}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Score"
                                                value={modalTraining.score || ''}
                                                fullWidth
                                                disabled={!isEditing}
                                                onChange={(e) => updateModalField('score', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            {isEditing ? (
                                                <FormControl fullWidth>
                                                    <InputLabel id="score-type-label">Score Type</InputLabel>
                                                    <Select
                                                        labelId="score-type-label"
                                                        value={modalTraining.scoreType || 'rx'}
                                                        label="Score Type"
                                                        onChange={(e) => updateModalField('scoreType', e.target.value)}
                                                    >
                                                        <MenuItem value="rx">RX</MenuItem>
                                                        <MenuItem value="sc">SC</MenuItem>
                                                        <MenuItem value="beg">BEGINNER</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            ) : (
                                                <TextField
                                                    label="Score Type"
                                                    value={getScoreTypeDisplay(modalTraining.scoreType)}
                                                    fullWidth
                                                    disabled
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        </Grid>
                                    </>
                                )}

                                {/* Exercises */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Workout image
                                    </Typography>

                                    {(modalWorkoutImagePreview || (modalWorkoutImage?.imageData && !removeModalWorkoutImage)) && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <Box
                                                component="img"
                                                src={modalWorkoutImagePreview || modalWorkoutImage?.imageData}
                                                alt="Training workout"
                                                onClick={() => setImageDialogOpen(true)}
                                                sx={{
                                                    width: '100%',
                                                    maxHeight: 280,
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    border: '1px solid #ddd',
                                                    cursor: 'zoom-in'
                                                }}
                                            />
                                        </Box>
                                    )}

                                    {isEditing && (
                                        <>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                Supported formats: JPG, PNG, WEBP, GIF, AVIF. Max file size: 15MB.
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Button variant="outlined" component="label" startIcon={<ZoomInIcon />}>
                                                    Select image
                                                    <input
                                                        hidden
                                                        type="file"
                                                        accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                                                        onChange={handleModalWorkoutImageChange}
                                                    />
                                                </Button>
                                                {(modalWorkoutImagePreview || modalWorkoutImage?.imageData) && (
                                                    <Button variant="outlined" color="error" onClick={handleRemoveModalWorkoutImage}>
                                                        Remove image
                                                    </Button>
                                                )}
                                            </Box>
                                            {modalImageError && (
                                                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                                    {modalImageError}
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                </Grid>

                                {modalTraining && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Exercises:
                                        </Typography>
                                        {isEditing ? (
                                            <>
                                                {/* Formatting toolbar */}
                                                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="body2" sx={{ mr: 1 }}>Formatting:</Typography>
                                                    <Tooltip title="Bold">
                                                        <IconButton size="small" onClick={() => applyFormatting('bold', null, modalExercisesRef)}>
                                                            <FormatBoldIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Italic">
                                                        <IconButton size="small" onClick={() => applyFormatting('italic', null, modalExercisesRef)}>
                                                            <FormatItalicIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Box sx={{ position: 'relative' }}>
                                                        <Tooltip title="Text Color">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setSelectedColorOption(prev => prev === 'modal-exercises' ? null : 'modal-exercises')}
                                                            >
                                                                <FormatColorTextIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        {selectedColorOption === 'modal-exercises' && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: 0,
                                                                zIndex: 1000,
                                                                width: '200px',
                                                                backgroundColor: 'white',
                                                                border: '1px solid #ddd',
                                                                borderRadius: 1,
                                                                boxShadow: 3,
                                                                p: 1
                                                            }}>
                                                                <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                                                                    Select color:
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {colorOptions.map((option) => (
                                                                        <Tooltip key={option.name} title={option.name}>
                                                                            <Box
                                                                                sx={{
                                                                                    width: 24,
                                                                                    height: 24,
                                                                                    backgroundColor: option.color,
                                                                                    border: '1px solid #888',
                                                                                    borderRadius: 0.5,
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                                onClick={() => {
                                                                                    applyFormatting('color', option.color, modalExercisesRef);
                                                                                    setSelectedColorOption(null);
                                                                                }}
                                                                            />
                                                                        </Tooltip>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>

                                                <FormControl fullWidth>
                                                    <Box
                                                        ref={modalExercisesRef}
                                                        contentEditable
                                                        suppressContentEditableWarning
                                                        onInput={(e) => {
                                                            const value = e.currentTarget.innerHTML;
                                                            if (value.length <= 2000) {
                                                                setModalTraining((prev) => ({
                                                                    ...prev,
                                                                    exercises: value
                                                                }));
                                                            }
                                                        }}
                                                        data-placeholder="Enter your exercises, sets, reps and weights (select text to format)"
                                                        sx={{
                                                            minHeight: '120px',
                                                            maxHeight: '400px',
                                                            overflowY: 'auto',
                                                            padding: '10px',
                                                            border: '1px solid #AAAAAA',
                                                            borderRadius: '4px',
                                                            fontSize: '14px',
                                                            lineHeight: '1.5',
                                                            fontFamily: 'inherit',
                                                            '&:focus': {
                                                                outline: 'none',
                                                                borderColor: '#1976d2',
                                                                borderWidth: '2px'
                                                            },
                                                            '&:empty:before': {
                                                                content: 'attr(data-placeholder)',
                                                                color: '#999'
                                                            }
                                                        }}
                                                    />
                                                    {modalTraining.exercises && modalTraining.exercises.length >= 1800 && (
                                                        <Typography
                                                            variant="caption"
                                                            color={modalTraining.exercises.length >= 2000 ? "error" : "warning"}
                                                            sx={{mt: 1}}
                                                        >
                                                            {modalTraining.exercises.length >= 2000
                                                                ? "Maximum 2000 characters allowed. You've reached the limit."
                                                                : `${2000 - modalTraining.exercises.length} characters remaining.`}
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            </>
                                        ) : (
                                            <Paper
                                                elevation={1}
                                                sx={{
                                                    mb: 2,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    minHeight: '100px'
                                                }}
                                            >
                                                {typeof modalTraining.exercises === 'string' ? (
                                                    <Box
                                                        dangerouslySetInnerHTML={{
                                                            __html: DOMPurify.sanitize(modalTraining.exercises, {
                                                                ADD_ATTR: ['style'],
                                                                USE_PROFILES: {html: true}
                                                            })
                                                        }}
                                                        sx={{
                                                            whiteSpace: 'pre-line',
                                                            lineHeight: 1.35,
                                                            '& p': { margin: '0 0 0.25em 0' },
                                                            '& p:last-child': { marginBottom: 0 },
                                                        }}
                                                    />
                                                ) : Array.isArray(modalTraining.exercises) ? (
                                                    <Box
                                                        dangerouslySetInnerHTML={{
                                                            __html: DOMPurify.sanitize(
                                                                modalTraining.exercises.map(ex => ex.exerciseData || '').join('<br>'),
                                                                {
                                                                    ADD_ATTR: ['style'],
                                                                    USE_PROFILES: {html: true}
                                                                }
                                                            )
                                                        }}
                                                        sx={{
                                                            whiteSpace: 'pre-line',
                                                            lineHeight: 1.35,
                                                            '& p': { margin: '0 0 0.25em 0' },
                                                            '& p:last-child': { marginBottom: 0 },
                                                        }}
                                                    />
                                                ) : (
                                                    ''
                                                )}
                                            </Paper>
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </DialogContent>

                    <DialogActions sx={{p: 2}}>
                        <Box sx={{flexGrow: 1}}>
                                <WeightliftingPercentageCalculator/>
                            </Box>


                        {!isEditing ? (
                            <>
                                <IconButton
                                    onClick={handleDelete}
                                    aria-label="delete"
                                    sx={{
                                        color: 'white',
                                        bgcolor: '#d32f2f',
                                        '&:hover': {bgcolor: '#b71c1c'}
                                    }}
                                >
                                    <DeleteIcon/>
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    onClick={handleEdit}
                                    aria-label="edit"
                                >
                                    <EditIcon/>
                                </IconButton>

                            </>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                startIcon={<SaveIcon/>}
                                disabled={isSavingEdit}
                            >
                                {isSavingEdit ? 'Saving...' : 'Save Changes'}
                            </Button>
                        )}

                        {modalTraining?.type === 'WOD' && modalTraining?.wodName && !isEditing && (
                            <Button
                                onClick={handleAddToRecords}
                                variant="outlined"
                                startIcon={<EmojiEventsIcon/>}
                                sx={{
                                    lineHeight: 1.05,
                                    py: { xs: 0.5, sm: 0.75 },
                                    px: { xs: 1, sm: 1.5 },
                                    fontSize: { xs: '0.72rem', sm: '0.875rem' },
                                    whiteSpace: 'normal',
                                    textAlign: 'center',
                                    '& .MuiButton-startIcon': {
                                        mr: { xs: 0.5, sm: 1 }
                                    }
                                }}
                            >
                                Add to Records
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onClick={isEditing ? handleCancelEdit : closeModal}
                            color="inherit"
                        >
                            {isEditing ? "Cancel" : "Close"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={imageDialogOpen}
                    onClose={() => setImageDialogOpen(false)}
                    maxWidth="lg"
                    fullWidth
                >
                    <Box sx={{ position: 'relative', backgroundColor: 'black' }}>
                        <IconButton
                            onClick={() => setImageDialogOpen(false)}
                            sx={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                zIndex: 1,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.45)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Box
                            component="img"
                            src={modalWorkoutImagePreview || modalWorkoutImage?.imageData || ''}
                            alt="Training workout full size"
                            sx={{
                                width: '100%',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                backgroundColor: 'black'
                            }}
                        />
                    </Box>
                </Dialog>

                {/* New Add to Records Dialog with Comment Field */}
                <Dialog
                    open={addToRecordsDialogOpen}
                    onClose={() => setAddToRecordsDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Add to Records</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{mb: 2}}>
                            Do you want to add this WOD to your records?
                        </DialogContentText>

                        <Box sx={{mt: 2}}>
                            <Typography><strong>WOD:</strong> {modalTraining?.wodName ? modalTraining.wodName.toUpperCase() : "WOD"}
                            </Typography>
                            <Typography><strong>Date:</strong> {modalTraining ? new Date(modalTraining.date).toLocaleDateString() : ""}
                            </Typography>
                            <Typography><strong>Score:</strong> {modalTraining?.score || ""}</Typography>
                            <Typography><strong>Type:</strong> {modalTraining?.wodType?.toUpperCase() || ""}
                            </Typography>
                        </Box>

                        {/* Comment Field */}
                        <TextField
                            label="Comment (Optional)"
                            multiline
                            rows={3}
                            fullWidth
                            value={recordComment}
                            onChange={(e) => setRecordComment(e.target.value)}
                            margin="normal"
                            placeholder="Enter any notes about your performance..."
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setAddToRecordsDialogOpen(false)}
                            color="inherit"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={submitAddToRecords}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Success Dialog */}
                <Dialog
                    open={successDialogOpen}
                    onClose={() => setSuccessDialogOpen(false)}
                >
                    <DialogTitle>Success</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {dialogMessage}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSuccessDialogOpen(false)} color="primary">
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Error Dialog */}
                <Dialog
                    open={errorDialogOpen}
                    onClose={() => setErrorDialogOpen(false)}
                >
                    <DialogTitle>Error</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {dialogMessage}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setErrorDialogOpen(false)} color="primary">
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Confirmation Dialog */}
                <Dialog
                    open={confirmDialogOpen}
                    onClose={() => setConfirmDialogOpen(false)}
                >
                    <DialogTitle>{confirmDialogTitle}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {confirmDialogMessage}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (confirmAction) confirmAction();
                                setConfirmDialogOpen(false);
                            }}
                            color="primary"
                            variant="contained"
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
                {/* Month Trainings Feed Modal */}
                <Dialog
                    open={monthTrainingsModalOpen}
                    onClose={() => setMonthTrainingsModalOpen(false)}
                    maxWidth="md"
                    fullWidth
                    fullScreen={isMobile}
                    sx={{'& .MuiDialog-paper': {height: '90vh'}}}
                >
                    <DialogTitle sx={{sticky: 'top', zIndex: 1, bgcolor: 'background.paper'}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                {getTypeIcon(selectedType)}
                                <Typography variant="h6" component="div" sx={{ml: 1}}>
                                    {selectedType} Trainings - {currentMonth.toLocaleDateString('en', {
                                    month: 'long',
                                    year: 'numeric'
                                })}
                                </Typography>
                            </Box>
                            <IconButton onClick={() => setMonthTrainingsModalOpen(false)} edge="end">
                                <CloseIcon/>
                            </IconButton>
                        </Box>
                    </DialogTitle>

                    <DialogContent
                        dividers
                        sx={{
                            p: 1,
                            '&::-webkit-scrollbar': {width: '6px'},
                            '&::-webkit-scrollbar-track': {bgcolor: 'transparent'},
                            '&::-webkit-scrollbar-thumb': {bgcolor: 'rgba(0,0,0,0.2)', borderRadius: '3px'}
                        }}
                    >
                        {getMonthTrainingsByType(selectedType).length === 0 ? (
                            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200}}>
                                <Typography variant="body1" color="text.secondary">
                                    No {selectedType} trainings found for this month.
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                {getMonthTrainingsByType(selectedType).map((training) => (
                                    <Card
                                        key={training.id}
                                        sx={{
                                            border: `2px solid ${theme.palette.divider}`,
                                            borderRadius: 2,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {/* Header koos kuupäeva ja tüübiga */}
                                        <CardHeader
                                            avatar={getTypeIcon(training.type)}
                                            title={
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                    <Typography variant="h6">
                                                        {new Date(training.date).toLocaleDateString('et', {
                                                            day: 'numeric',
                                                            month: 'long',
                                                            year: 'numeric'
                                                        })}
                                                    </Typography>
                                                    {training.type === 'WOD' && training.wodName && (
                                                        <Typography variant="h6" color="primary">
                                                            - {training.wodName}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }

                                            action={
                                                training.score && (
                                                    <Box sx={{textAlign: 'right'}}>
                                                        <Typography variant="body2"
                                                                    color="text.secondary">Score</Typography>
                                                        <Typography variant="h6"
                                                                    color="primary">{training.score}</Typography>
                                                    </Box>
                                                )
                                            }
                                        />

                                        {/* Harjutuste sisu */}
                                        <CardContent sx={{pt: 1}}>
                                            {training.exercises && (
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        bgcolor: 'grey.50',
                                                        p: 2,
                                                        borderRadius: 1,
                                                        border: `1px solid ${theme.palette.divider}`
                                                    }}
                                                >
                                                    {typeof training.exercises === 'string' ? (
                                                        <Box
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(training.exercises, {
                                                                    ADD_ATTR: ['style'],
                                                                    USE_PROFILES: {html: true}
                                                                })
                                                            }}
                                                            sx={{
                                                                whiteSpace: 'pre-line',
                                                                lineHeight: 1.35,
                                                                '& p': { margin: '0 0 0.25em 0' },
                                                                '& p:last-child': { marginBottom: 0 },
                                                            }}
                                                        />
                                                    ) : Array.isArray(training.exercises) ? (
                                                        <Box
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(
                                                                    training.exercises.map(ex => ex.exerciseData || '').join('<br>'),
                                                                    {
                                                                        ADD_ATTR: ['style'],
                                                                        USE_PROFILES: {html: true}
                                                                    }
                                                                )
                                                            }}
                                                            sx={{
                                                                whiteSpace: 'pre-line',
                                                                lineHeight: 1.35,
                                                                '& p': { margin: '0 0 0.25em 0' },
                                                                '& p:last-child': { marginBottom: 0 },
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography color="text.secondary" fontStyle="italic">
                                                            No exercises recorded
                                                        </Typography>
                                                    )}
                                                </Paper>
                                            )}
                                        </CardContent>


                                    </Card>
                                ))}
                            </Box>
                        )}
                    </DialogContent>
                </Dialog>
            </StyledContainer>
        </AppTheme>
    );
}